import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MdsIcon } from '@mds/icons';
import { EpdFamilyStatus } from '@pages/dashboards/epd-dashboard/utils/epd-dashboard.model';

export enum EPDRowActionMenuKeyEnum {
  ChangeStatus = 'status',
  InitializeEPD = 'initialize_epd',
  ChangeVerifier = 'internal_verifier',
  OpenLCA = 'open_lca',
  Publish = 'publish',
  SeeHistory = 'changes_history',
  DownloadArtifacts = 'download_latest_handout',
  ResumeEPD = 'resume_epd'
}

export interface EPDManagementResultItem {
  status: string;
  rid: string;
  name: string;
  level: number;
  family_rid: string;
  products_included: string[];
  is_reference: boolean;
  reference_product: string;
  type: string;
  menu_actions: { [key: string]: boolean };
  menuOptions?: any[];
  actions: { [key: string]: boolean };
  tag?: {
    text: string;
    icon: MdsIcon;
    type: string;
  };
  parents: string[];
  children: EPDManagementResultItem[];
  lc_model_rid: string;
  change_extrapolated_epd: boolean;
  pcr_rid: string;
  prop_PN: string;
  author_name: string;
  latest_epd_handout_download_link: string;
  external_verifier: string;
  version_major: number;
  version_minor: number;
  epd_standard_display?: string | null;
  latest_epd_family_user_log: string;
  external_status?: EPDExternalStatusData | null;
}

export interface EPDMenuSection {
  description: string;
  field: string;
  formSchema: any[];
  icon: MdsIcon;
  key: EPDRowActionMenuKeyEnum;
  lines: any[];
  name: string;
  options: any[];
  sortlevel: number;
}

export interface EPDRowActionSection {
  description: string;
  key: EPDRowActionMenuKeyEnum;
  name: string;
  options: { name: string; value: string }[];
  field: string;
  formSchema: any[];
}

export interface EPDMenuGroup {
  key: string;
  name: string;
  sections: EPDMenuSection[];
}

export interface EPDMenus {
  taxonomy_family: EPDMenuGroup[];
}

export interface RowActions {
  internal_verifier: EPDMenuSection;
  status: EPDRowActionSection;
}

export interface EPDManagementResults {
  data: EPDManagementResultItem[];
  formSchema: {
    menus: EPDMenus;
    row_actions: RowActions;
  };
}

export interface EPDManagementResult {
  data: EpdFamilyStatus;
  formSchema: {
    menus: EPDMenus;
    row_actions: RowActions;
  };
}

export const getResumeFormSchema = (menus: EPDMenus | null | undefined): EPDMenuSection['formSchema'] =>
  menus?.taxonomy_family
    ?.flatMap((group) => group.sections)
    .find((section) => section.key === EPDRowActionMenuKeyEnum.ResumeEPD)?.formSchema ?? [];

export interface EPDExternalStatusData {
  external_epd_status: string | null;
  published_dataset_version: string | null;
  registration_number: string | null;
  date_of_last_revision: string | null;
  publication_date_of_epd: string | null;
  data_set_valid_until: string | null;
  last_synced_at: string | null;
  ibu_data_url: string | null;
}

export interface EPDExternalStatusResponse {
  data: EPDExternalStatusData;
}

export interface UpdateStatusResponse {
  rid: string;
  updated: {
    status: string;
    updated_on: string;
    edges_updated: any[];
  };
}

export interface UpdateExtrapolateEPDResponse {
  rid: string;
  updated: {
    updated_on: string;
    edges_updated: string[];
  };
}

export interface UpdateVerifierResponse {
  rid: string;
  updated: {
    updated_on: string;
    edges_updated: string[];
  };
}

export interface InitializePayload {
  pcr_rid: string;
  update_type: string;
  languages: string[];
  is_external: boolean;
  epd_version?: 'minor' | 'major';
  epd_usage?: 'external' | 'internal';
  regenerate_lc_models: boolean;
  internal_verifier: string;
  external_verifier: string;
  standard: string;
  comment: string;
}

export interface InitializeEpdFormSchemaField {
  description: string;
  mandatory: boolean;
  defaultValue: string | string[] | null;
  multiple: boolean;
  formtype: string;
  section: string;
  title: string;
  sortlevel: number;
  fieldtype: string;
  enum: { name: string; value: string }[] | null;
  key: string;
  options: string | { name: string; value: string }[] | null;
  create_new: string | null;
  default_options: string | null;
  default_option_key: string | null;
  radioOption: boolean;
  hide_options_key: string | null;
  hide_options_value: string | null;
  vertical: boolean;
}

export interface InitializeEpdStandardField extends InitializeEpdFormSchemaField {
  enum: { name: string; value: string }[];
}

export type InitializeEpdFormSchemas = Record<string, InitializeEpdFormSchemaField[]>;

export interface InitializeEpdFormSchemaResponse {
  standards: InitializeEpdStandardField;
  formschemas: InitializeEpdFormSchemas;
  warning: string | null;
}

@Injectable({ providedIn: 'root' })
export class EPDManagementService {
  private readonly httpClient = inject(HttpClient);

  getEPDManagementResults() {
    return this.httpClient.get<EPDManagementResults>('/api/v1/epd-management');
  }

  updateStatus(familyRid: string, newStatus: string, date?: string) {
    const payload: { status: string; externally_verified_date?: string } = {
      status: newStatus
    };

    if (date) {
      payload.externally_verified_date = date;
    }

    return this.httpClient.put<UpdateStatusResponse>(
      `/api/v1/composer/route/${familyRid.replace('#', '')}`,
      payload
    );
  }

  updateExtrapolateEPD(familyRid: string, productGroupRid: string, newState: boolean) {
    return this.httpClient.put<UpdateExtrapolateEPDResponse>(
      `/api/v1/composer/route/${familyRid.replace('#', '')}`,
      {
        product_group_rid: productGroupRid,
        extrapolate_epd: newState
      }
    );
  }

  updateVerifier(familyRid: string, verifierRid: string) {
    return this.httpClient.put<UpdateVerifierResponse>(
      `/api/v1/composer/route/${familyRid.replace('#', '')}`,
      {
        internal_verifier_rid: verifierRid
      }
    );
  }

  getInitializeEpdFormSchema(familyRid: string) {
    return this.httpClient.get<InitializeEpdFormSchemaResponse>(
      `/api/v1/epd-management/${familyRid.replace('#', '')}/initialize-epd/form-schema`
    );
  }

  initializeEPD(familyRid: string, payload: InitializePayload) {
    return this.httpClient.post<{ message: string }>(
      `/api/v1/epd-management/${familyRid.replace('#', '')}/initialize-epd`,
      payload
    );
  }

  publish(familyRid: string) {
    return this.httpClient.post<{ message: string }>(
      `/api/v1/epd-management/${familyRid.replace('#', '')}/publish`,
      null
    );
  }

  resume(familyRid: string, regenerateModels: 'Yes' | 'No') {
    return this.httpClient.post<{ message: string }>(
      `/api/v1/epd-management/${familyRid.replace('#', '')}/resume-epd`,
      {
        regenerate_lc_models: regenerateModels === 'Yes'
      }
    );
  }

  fetchEPDFamilyStatus(familyRid) {
    return this.httpClient.get<EPDManagementResult>(`/api/v1/epd-management/${familyRid}/status`);
  }

  fetchExternalStatus(familyRid: string) {
    return this.httpClient.get<EPDExternalStatusResponse>(`/api/v1/epd-management/${familyRid}/external-status`);
  }

  fetchArtifactDownloadLink(url) {
    return this.httpClient.get<{ url: string }>(url);
  }

  fetchChildren(familyRid: string) {
    return this.httpClient.get<EPDManagementResults>(
      `/api/v1/epd-management/${familyRid.replace('#', '')}/expand`
    );
  }

  fetchResultsByPartNumber(searchTerm: string) {
    return this.httpClient.get<EPDManagementResults>(
      `/api/v1/epd-management/search?partNumber=${searchTerm}`
    );
  }

  uploadCompanyLogo(teamRid, file) {
    const formData = new FormData();
    formData.append('company_logo', file, file.name);
    return this.httpClient.post(`/api/v1/team/epd-properties/${teamRid.replace('#', '')}`, formData);
  }

  fetchCompanyLogoAsBlob(imgsrc: string) {
    return this.httpClient.get(imgsrc, {
      responseType: 'blob',
      headers: { 'Cross-Origin-Resource-Policy': 'cross-origin' }
    });
  }

  updateCompanyLogo(teamRid, payload) {
    return this.httpClient.put(`/api/v1/team/epd-properties/${teamRid.replace('#', '')}`, payload);
  }

  deleteCompanyLogo(teamRid) {
    return this.httpClient.delete(`/api/v1/team/company-logo/${teamRid.replace('#', '')}`);
  }
}
